import React, { useEffect } from 'react'
import { Link, router, Head } from '@inertiajs/react'
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  CreditCard,
  RefreshCw,
  Rocket,
  MailCheck,
  LifeBuoy,
} from 'lucide-react'
import ClaimShell from '@/components/claim/ClaimShell'
import { SUPPORT_EMAIL } from '@/lib/brand'
import { Button } from '@/components/ui/button'

interface PaymentStatusProps {
  subscriptionStatus: string | null
  // The single backend gate (Account#live_allowed?). True once the first charge's webhook lands.
  liveAllowed: boolean
  // The latest Payment's PaymentIntent status (pending | succeeded | failed | requires_action),
  // or null before any charge is recorded. Read straight off our Payment row (source of truth).
  paymentStatus: string | null
  // The terminal lapse state: charge failed and the 5-day grace has lapsed (access frozen).
  graceExpired: boolean
  // Where "View billing" goes — SERVED by the backend, never assembled here. Billing lives under
  // /a/:account_slug/settings/billing and the claim funnel carries no business in its path, so this
  // page cannot derive it. null when no account resolves, in which case the link is omitted.
  billingPath: string | null
}

// How often the activating state re-reads the account's billing state while it polls. The first
// off-session charge settles out-of-band (scheduler → payment_intent.succeeded webhook), so we
// refresh until liveAllowed flips (or a terminal state — requires_action / grace-expired).
const POLL_INTERVAL_MS = 4000

// PaymentStatus (FE3) — replaces the post-checkout landing. After the card is captured in-app,
// the first charge settles out-of-band, so this page is a pure read of the account's current
// billing state that POLLS to convergence:
//   * liveAllowed true                → activated → dashboard;
//   * paymentStatus 'requires_action' → SCA hand-off ("check your email");
//   * graceExpired                    → terminal lapse ("contact support");
//   * otherwise                       → activating (keep polling).
export default function PaymentStatus({
  subscriptionStatus,
  liveAllowed,
  paymentStatus,
  graceExpired,
  billingPath,
}: PaymentStatusProps) {
  const requiresAction = paymentStatus === 'requires_action'
  // Only the activating state polls — the three terminal states (activated / SCA / lapsed) stop.
  const isPolling = !liveAllowed && !requiresAction && !graceExpired

  useEffect(() => {
    if (!isPolling) return
    // A bare GET re-render of /claim/payment/status (same page) — router.reload, not navigation.
    // `only` keeps the round-trip lean: just the four status props.
    const id = window.setInterval(() => {
      router.reload({ only: ['subscriptionStatus', 'liveAllowed', 'paymentStatus', 'graceExpired'] })
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [isPolling])

  return (
    <ClaimShell currentStep={3}>
      <Head title="Payment status" />
      <div className="mx-auto max-w-lg pb-8">
        {liveAllowed ? (
          <ActivatedState billingPath={billingPath} />
        ) : graceExpired ? (
          <GraceExpiredState />
        ) : requiresAction ? (
          <RequiresActionState />
        ) : (
          <ActivatingState subscriptionStatus={subscriptionStatus} billingPath={billingPath} />
        )}
      </div>
    </ClaimShell>
  )
}

// "View billing", pointed at THIS claimant's business. Rendered only when the backend resolved one —
// a claim that somehow has no account gets no link rather than a guessed one.
function BillingLink({ billingPath }: { billingPath: string | null }) {
  if (!billingPath) return null

  return (
    <Link
      href={billingPath}
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-border/60 bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
    >
      <CreditCard className="h-4 w-4" />
      View billing
    </Link>
  )
}

// Success — the first charge settled and access is granted. The customer can take properties live.
function ActivatedState({ billingPath }: { billingPath: string | null }) {
  return (
    <div className="animate-fade-in-up overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-emerald-50 to-white px-8 pt-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight text-foreground">You&apos;re all set</h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          Your subscription is active. Your properties start in{' '}
          <span className="font-medium text-foreground">preview</span>. Open each one and choose{' '}
          <span className="font-medium text-foreground">Go live</span> when you&apos;re ready to start
          publishing optimised rates to your PMS.
        </p>
      </div>

      <div className="space-y-3 p-8 pt-6">
        <Link
          href="/"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
        >
          <Rocket className="h-4 w-4" />
          Go to your properties
          <ArrowRight className="h-4 w-4" />
        </Link>
        <BillingLink billingPath={billingPath} />
      </div>
    </div>
  )
}

// Pending — the card was saved but the first charge's webhook hasn't reconciled yet. Auto-polls;
// a manual "Check again" is offered for the impatient.
function ActivatingState({
  subscriptionStatus,
  billingPath,
}: {
  subscriptionStatus: string | null
  billingPath: string | null
}) {
  return (
    <div className="animate-fade-in-up overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-primary/5 to-white px-8 pt-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight text-foreground">
          Activating your subscription…
        </h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          Thanks, your card is saved. We&apos;re confirming your first payment with our payment
          provider. This usually takes only a few moments.
        </p>
        {subscriptionStatus && (
          <p className="mt-3 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
            Status: {subscriptionStatus.replace('_', ' ')}
          </p>
        )}
      </div>

      <div className="space-y-3 p-8 pt-6">
        {/* Same page — a fresh GET of /claim/payment/status — so router.reload, not navigation. */}
        <Button onClick={() => router.reload()} className="w-full rounded-lg">
          <RefreshCw className="mr-2 h-4 w-4" />
          Check again
        </Button>
        <BillingLink billingPath={billingPath} />
      </div>
    </div>
  )
}

// SCA / 3D Secure — the off-session charge needs the customer to re-authenticate via the link the
// backend emailed (SendReAuthLinkMutation). We stop polling and hand off (R-SCA).
function RequiresActionState() {
  return (
    <div className="animate-fade-in-up overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-amber-50 to-white px-8 pt-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
          <MailCheck className="h-8 w-8 text-amber-600" />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight text-foreground">
          Check your email to verify
        </h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          Your bank needs to verify this payment. We&apos;ve emailed you a secure link. Follow it
          to confirm, and your subscription activates straight away.
        </p>
      </div>

      <div className="space-y-3 p-8 pt-6">
        {/* Once they confirm via the email link, re-check this page for the activated state. */}
        <Button onClick={() => router.reload()} className="w-full rounded-lg">
          <RefreshCw className="mr-2 h-4 w-4" />
          I&apos;ve verified, check again
        </Button>
      </div>
    </div>
  )
}

// Terminal lapse — the charge failed and the 5-day grace window expired, freezing access. No
// amount of polling recovers this; the customer must contact support.
function GraceExpiredState() {
  return (
    <div className="animate-fade-in-up overflow-hidden rounded-xl border border-rose-200 bg-white shadow-sm">
      <div className="bg-gradient-to-b from-rose-50 to-white px-8 pt-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100">
          <LifeBuoy className="h-8 w-8 text-rose-600" />
        </div>
        <h1 className="mt-5 text-xl font-bold tracking-tight text-foreground">
          We couldn&apos;t complete your payment
        </h1>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted-foreground">
          Your payment didn&apos;t go through and the grace period has ended, so access is paused.
          Get in touch and we&apos;ll help you get set up. Your rate data is safe and waiting.
        </p>
      </div>

      <div className="space-y-3 p-8 pt-6">
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
        >
          <LifeBuoy className="h-4 w-4" />
          Contact support
        </a>
      </div>
    </div>
  )
}
